import { useEffect, useRef, useCallback } from 'react'
import { fetchWithRefresh } from '@/utils/fetchWithRefresh'

// Global registry to prevent duplicate polling for the same scan
const activePollers = new Map<string, boolean>()

// Track which scans have already shown warning toasts
const warningsShown = new Set<string>()

interface ScanWarning {
  severity: 'high' | 'medium' | 'low'
  tool: string
  type: 'timeout' | 'error'
  message: string
  impact: string
  recommendation: string
  duration: number
}

interface ScanStatus {
  status: 'queued' | 'running' | 'completed' | 'failed' | 'partially_completed'
  progress: number
  time_elapsed: number
  url: string
  scan_type: string
  created_at?: string
  security_score?: number
  vulnerabilities_count?: number
  error?: string
  warnings?: ScanWarning[]
  warnings_count?: number
  scan_completeness?: string
  queue_position?: number
  estimated_wait_minutes?: number
}

interface UseScanPollingOptions {
  scanId: string
  enabled: boolean
  onUpdate?: (status: ScanStatus) => void
  onComplete?: (status: ScanStatus) => void
  onError?: (error: string) => void
  toast?: (options: {
    title: string
    description?: string
    variant?: "default" | "success" | "destructive" | "confirmation"
  }) => void
}

/**
 * Custom hook to poll scan status with exponential backoff
 *
 * Polling intervals (reduced to prevent network overload):
 * - First 1 minute: every 5 seconds
 * - 1-3 minutes: every 10 seconds
 * - After 3 minutes: every 15 seconds
 *
 * Auto-stops when scan is completed or failed
 */
export function useScanPolling({
  scanId,
  enabled,
  onUpdate,
  onComplete,
  onError,
  toast
}: UseScanPollingOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollCountRef = useRef(0)
  const startTimeRef = useRef<number>(Date.now())
  const isPollingRef = useRef(false) // Prevent duplicate polling

  // Use refs for callbacks to prevent re-creating fetchStatus on every render
  const onUpdateRef = useRef(onUpdate)
  const onCompleteRef = useRef(onComplete)
  const onErrorRef = useRef(onError)
  const toastRef = useRef(toast)

  // Update refs when callbacks change
  useEffect(() => {
    onUpdateRef.current = onUpdate
    onCompleteRef.current = onComplete
    onErrorRef.current = onError
    toastRef.current = toast
  }, [onUpdate, onComplete, onError, toast])

  const fetchStatus = useCallback(async () => {
    // Prevent overlapping requests
    if (isPollingRef.current) {
      return null
    }

    isPollingRef.current = true

    try {
      const response = await fetchWithRefresh(`/api/scan/status/${scanId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        if (response.status === 404) {
          console.error(`[useScanPolling] Scan not found`)

          // Show toast for scan not found (likely deleted due to connectivity failure)
          if (!warningsShown.has(scanId) && toastRef.current) {
            warningsShown.add(scanId)
            toastRef.current({
              title: 'Site unreachable',
              description: 'Unable to reach the target site. The scan was cancelled because the site could not be accessed.',
              variant: "destructive"
            })
          }

          onErrorRef.current?.('Scan not found')
          isPollingRef.current = false
          return null
        }
        throw new Error(`Failed to fetch scan status: ${response.status}`)
      }

      const data: ScanStatus = await response.json()

      // Call update callback using ref
      onUpdateRef.current?.(data)

      // Check if scan is complete, partially completed, or failed
      if (data.status === 'completed' || data.status === 'partially_completed' || data.status === 'failed') {
        // Show toast notifications for completed scans (only if toast function provided)
        if (!warningsShown.has(scanId) && toastRef.current) {
          warningsShown.add(scanId)

          if (data.status === 'partially_completed' && data.warnings && data.warnings.length > 0) {
            // Show warning toast for partially completed scans
            toastRef.current({
              title: `Scan completed with ${data.warnings.length} warning${data.warnings.length > 1 ? 's' : ''}`,
              description: data.scan_completeness || `Some tools failed to complete`,
              variant: "default"
            })

            // Show individual toasts for high severity warnings
            const highSeverityWarnings = data.warnings.filter(w => w.severity === 'high')
            if (highSeverityWarnings.length > 0 && highSeverityWarnings.length <= 3) {
              highSeverityWarnings.forEach(warning => {
                toastRef.current?.({
                  title: `${warning.tool.toUpperCase()}: ${warning.message}`,
                  description: warning.impact,
                  variant: "destructive"
                })
              })
            }
          } else if (data.status === 'completed') {
            // Show success toast for fully completed scans
            toastRef.current({
              title: 'Scan completed successfully',
              description: 'All security tools completed without issues',
              variant: "success"
            })
          } else if (data.status === 'failed') {
            // Check if this is a connectivity failure
            const isConnectivityFailure = data.error && (
              data.error.includes('404') ||
              data.error.includes('not found') ||
              data.error.includes('Unable to connect') ||
              data.error.includes('site may be down') ||
              data.error.includes('domain may not exist') ||
              data.error.includes('timed out') ||
              data.error.includes('Connection to')
            )

            // Show appropriate error toast
            if (isConnectivityFailure) {
              toastRef.current({
                title: 'Site unreachable',
                description: data.error || 'Unable to reach the target site. Please verify the URL and check if the site is accessible.',
                variant: "destructive"
              })
            } else {
              toastRef.current({
                title: 'Scan failed',
                description: data.error || 'The scan encountered an error',
                variant: "destructive"
              })
            }
          }
        }

        // Give a small delay to ensure database is fully updated
        setTimeout(() => {
          onCompleteRef.current?.(data)
        }, 500)
        isPollingRef.current = false
        return null // Stop polling
      }

      isPollingRef.current = false
      return data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error(`[useScanPolling] Error:`, errorMessage)
      onErrorRef.current?.(errorMessage)
      isPollingRef.current = false
      return null
    }
  }, [scanId])  // Only depend on scanId now!

  const getPollingInterval = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current

    // First 1 minute: poll every 5 seconds (reduced from 2s)
    if (elapsed < 60000) {
      return 5000
    }

    // 1-3 minutes: poll every 10 seconds (reduced from 5s)
    if (elapsed < 180000) {
      return 10000
    }

    // After 3 minutes: poll every 15 seconds (increased from 10s)
    return 15000
  }, [])

  useEffect(() => {
    if (!enabled || !scanId) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    // Check if this scan is already being polled by another component
    // This needs to be atomic - check and set in one operation
    if (activePollers.has(scanId)) {
      console.warn(`[useScanPolling] ${scanId.substring(0, 8)} already being polled, skipping`)
      return
    }

    // Register this poller IMMEDIATELY before any async operations
    activePollers.set(scanId, true)

    // Reset refs
    startTimeRef.current = Date.now()
    pollCountRef.current = 0

    // Start polling immediately
    const pollOnce = async () => {
      // Check if polling was already cancelled (component unmounted)
      if (!activePollers.has(scanId)) {
        return
      }

      const status = await fetchStatus()

      // Check again after async operation
      if (!activePollers.has(scanId)) {
        return
      }

      // If status is null (completed/failed/error), stop polling
      if (!status) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        // Remove from active pollers
        activePollers.delete(scanId)
        return
      }

      pollCountRef.current++
    }

    // Initial poll
    pollOnce()

    // Set up interval - simple fixed interval based on initial state
    // We don't dynamically update it to avoid creating overlapping intervals
    const interval = getPollingInterval()
    intervalRef.current = setInterval(pollOnce, interval)

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      // Remove from active pollers
      activePollers.delete(scanId)
      // Remove from warnings shown (allow re-showing if component remounts)
      warningsShown.delete(scanId)
      // Reset polling flag
      isPollingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scanId])  // Only depend on enabled and scanId - fetchStatus and getPollingInterval accessed via closure

  return {
    stopPolling: () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }
}
